import { Selector } from "react-redux";

import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { EMPTY_ARRAY, invariant } from "@multiweb/reactUtils";
import { IAppState } from "@multiweb/types/react";
import { ControlReduxRawValue, ControlsInstance, ControlsKey, IControlsState, MergeValidationErrorsParams } from "./types";
import { areLocalizedErrorsEqual, IExplicitValidationError, ILocalizedExplicitValidationError, isLocalizedErrorEqual } from "./validation";

/**
 * Získá z globálního stavu podstrom patřící modulu `controls`.
 * @param state
 */
const getModel: Selector<IAppState, IControlsState> = (state) => state.controls;

/**
 * Získá podstrom stavu patřící konkrétnímu poli (komponentě).
 * @param state
 * @param controlsKey Klíč pole sloužící k jeho jednoznačné identifikaci.
 * @throws Vyhodí chybu v případě, že pole se zadaným klíčem neexistuje.
 */
export const getControlsInstance: Selector<IAppState, ControlsInstance, ControlsKey> = (state, controlsKey) => {
    const controlsInstance = getModel(state).controlsInstances[controlsKey];
    if (!controlsInstance) {
        throw new Error(`[Controls] - Controls value with key ${controlsKey} is not initialized.`);
    }
    return controlsInstance;
};

/**
 * Získá vnitřní hodnotu pole.
 * @param state
 * @param controlsKey Klíč pole sloužící k jeho jednoznačné identifikaci.
 */
export const getRawValue: Selector<IAppState, ControlReduxRawValue | undefined, ControlsKey> = (state, controlsKey) => getControlsInstance(state, controlsKey).rawValue ?? undefined;

/**
 * Získá výchozí hodnotu pole.
 * @param state
 * @param controlsKey Klíč pole sloužící k jeho jednoznačné identifikaci.
 */
export const getInitialRawValue: Selector<IAppState, ControlReduxRawValue | undefined, ControlsKey> = (state, controlsKey) => getControlsInstance(state, controlsKey).initialRawValue ?? undefined;

/**
 * Získá validační chyby evidované ve stavu k vnitřní hodnotě pole.
 * @param state
 * @param controlsKey Klíč pole sloužící k jeho jednoznačné identifikaci.
 */
export const getErrors: Selector<IAppState, ReadonlyArray<IExplicitValidationError>, ControlsKey> = (state, controlsKey) => getControlsInstance(state, controlsKey).errors;

/**
 * Převádí instanci {@link IExplicitValidationError} na {@link ILocalizedExplicitValidationError}.
 * @param state Redux stav. Z něj se vezmou lokalizované hlášky.
 * @param error Chyba k převodu.
 */
export const getLocalizedValidationError = (state: IAppState, error: IExplicitValidationError): ILocalizedExplicitValidationError => {
    if (error.message !== undefined) {
        invariant(!error.locKey, `[controls] locKey must not be defined when message is defined: ${error.message}`);
        invariant(!error.locArgs, `[controls] locArgs must not be defined when message is defined: ${error.message}`);
        return ({
            message: error.message,
        });
    } else {
        invariant(!!error.locKey, "[controls] locKey must be defined when message is not");
        const message = getPrimaryText(state, error.locKey!, error.locArgs) ?? "";
        return ({
            message,
        });
    }
};

/**
 * Sloučí validační chyby z reduxu s `externalErrors` do jediného seznamu a lokalizuje je. Duplicity zahazuje.
 * @returns **Vždy novou instanci**!
 * @see createStableGetMergedValidationErrors
 */
const getMergedValidationErrors: Selector<IAppState, ReadonlyArray<ILocalizedExplicitValidationError>, MergeValidationErrorsParams> = (
    state, { dataKey, externalErrors },
) => {
    const componentErrors = getErrors(state, dataKey);

    const result: ILocalizedExplicitValidationError[] = [];
    const isErrorUnique = ((localizedError: ILocalizedExplicitValidationError) => result.every((resultError) => !isLocalizedErrorEqual(localizedError, resultError)));

    [...componentErrors, ...externalErrors].forEach((error) => {
        const localizedError = getLocalizedValidationError(state, error);
        if (isErrorUnique(localizedError)) {
            result.push(localizedError);
        }
    });

    return result;
};

/**
 * Vytvoří a vrátí selektor, který:
 * - Sloučí validační chyby z reduxu s `externalErrors` do jediného seznamu a lokalizuje je. Duplicity zahazuje.
 * - Pamatuje si výsledek z posledního volání. Pokud aktuální volání vrací obsahově totéž, pak vrátí výsledek z posledního volání - kvůli zachování stejné instance.
 *
 * <br>Pozn. Selektor se volá pokaždé, když je nutné zvalidovat stav. Např. v případě komponenty {@link Edit} se může volat i více než 20x a toto chování je známé.
 * Více informací je možné nalézt v rámci tasku {@link https://tfs/Jadro/Jadro%20.NET/_workitems/edit/30219}.
 */
export const createStableGetMergedValidationErrors = (): Selector<IAppState, ReadonlyArray<ILocalizedExplicitValidationError>, MergeValidationErrorsParams> => {
    let previousMergedErrors: ReadonlyArray<ILocalizedExplicitValidationError> = EMPTY_ARRAY;

    return (state, params) => {
        const mergedErrors = getMergedValidationErrors(state, params);
        if (areLocalizedErrorsEqual(previousMergedErrors, mergedErrors)) {
            return previousMergedErrors;
        } else {
            previousMergedErrors = mergedErrors;
            return mergedErrors;
        }
    };
};
